package com.milta.web

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLSelectElement

/**
 * Retour client — un dropdown custom en div (l'ancien [org.jetbrains.compose.web.dom.Div]-based
 * pattern utilisé par WebDashboardScreen/WebHistoriqueScreen) ne rend pas comme un vrai select
 * natif et sa flèche n'est pas fiablement ancrée à droite. Un vrai `<select>` HTML résout les deux
 * (clavier, accessibilité, rendu natif) ; `appearance: none` masque sa flèche native pour la
 * remplacer par le même glyphe "▼" déjà utilisé ailleurs dans ces écrans, positionné en absolu afin
 * qu'il reste à droite quel que soit le texte de l'option sélectionnée.
 *
 * [options] : paire (valeur, libellé) — valeur `null` pour l'option "Tous" (encodée côté HTML en
 * chaîne vide, seule valeur d'attribut `value` qu'un `<option>` accepte).
 */
@Composable
internal fun WebNativeSelect(
    options: List<Pair<String?, String>>,
    selectedValue: String?,
    disabled: Boolean = false,
    minWidth: String = "200px",
    onSelect: (String?) -> Unit,
) {
    Div(attrs = { style { position(Position.Relative); property("min-width", minWidth) } }) {
        Select(
            attrs = {
                if (disabled) attr("disabled", "true")
                addEventListener("change") { event ->
                    val raw = (event.target as? HTMLSelectElement)?.value
                    onSelect(raw?.takeIf { it.isNotEmpty() })
                }
                style {
                    width(100.percent)
                    property("appearance", "none"); property("-webkit-appearance", "none"); property("-moz-appearance", "none")
                    property("border", "1px solid $WebBorder"); property("border-radius", "9px")
                    property("background", WebSurface); property("color", WebTextPrimary)
                    property("font-size", "13px"); property("font-weight", "600")
                    property("padding", "9px 30px 9px 12px"); property("box-sizing", "border-box")
                    property("cursor", if (disabled) "not-allowed" else "pointer")
                    property("opacity", if (disabled) "0.6" else "1")
                }
            },
        ) {
            options.forEach { (value, label) ->
                Option(
                    value = value ?: "",
                    attrs = { if (value == selectedValue) attr("selected", "true") },
                ) { Text(label) }
            }
        }
        Div(
            attrs = {
                style {
                    position(Position.Absolute)
                    property("top", "50%"); property("right", "12px"); property("transform", "translateY(-50%)")
                    property("pointer-events", "none")
                    property("font-size", "11px"); property("color", WebTextSecondary)
                }
            },
        ) { Text("▼") }
    }
}
